import { SocketClient } from "cbcore-ts"
import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHColor } from "./RHColor"
import { RHFileItemData, RHFileItemView, RHFileItemViewVariant } from "./RHFileItemView"
import { RHForms } from "./Utils_slop/RHDesignTokens"
import { referenceId } from "../../Core/RHReference"

export class RHFileListView extends UIView {

    static variant = {
        full: "full" as const,
        compactDownload: "compactDownload" as const
    }

    /**
     * Reuses the server-side bulk-ZIP endpoint (`GetBulkDownloadUrl`) to package
     * the given files into a single archive, then triggers a browser download.
     * Throws on failure — the caller owns its own loading / error feedback.
     * Shared so every "download these files as a ZIP" surface uses one mechanism.
     */
    static async downloadFilesAsZip(fileIds: string[]) {
        if (fileIds.length === 0) {
            return
        }

        const companyId = await RHCore.sharedInstance.companyAffiliationStore
            .activeCompanyIdAfterLoadingAffiliations()
        const zipData = (await SocketClient.GetBulkDownloadUrl({ fileIds, companyId })).result

        if (!zipData?.url) {
            throw new Error("Failed to get bulk download URL")
        }

        await RHFileListView.downloadFileFromUrl(zipData.url, zipData.fileName || "files.zip")
    }

    static async downloadFileFromUrl(url: string, fileName: string): Promise<void> {
        const response = await fetch(url)

        if (!response.ok) {
            throw new Error("Bulk download failed")
        }

        const blob = await response.blob()
        const objectURL = window.URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = objectURL
        anchor.download = fileName
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        window.URL.revokeObjectURL(objectURL)
    }

    readonly emptyStateLabel = new UITextView()
        .configuredWithObject({
            text: "Faili ei ole lisatud",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "14px"
            }
        })
        .addedAsSubviewToView(this)

    private fileViews: RHFileItemView[] = []
    private _files: RHFileItemData[] = []
    private _downloadEnabled: boolean = YES
    private _deleteEnabled: boolean = NO
    private _variant: RHFileItemViewVariant = RHFileListView.variant.full

    constructor(elementID?: string) {
        super(elementID);

        (this as RHFileListView).configureWithObject({
            backgroundColor: UIColor.transparentColor
        })

        this.emptyStateLabel.hidden = NO
    }

    set files(files: RHFileItemData[]) {
        this._files = files || []
        this.updateFileViews()
    }

    get files(): RHFileItemData[] {
        return this._files
    }

    set variant(value: RHFileItemViewVariant) {
        if (this._variant === value) {
            return
        }
        this._variant = value
        this.updateFileViews()
    }

    get variant(): RHFileItemViewVariant {
        return this._variant
    }

    /**
     * Fetches file metadata for the given IDs from the server, then populates
     * the list. Use this instead of setting .files directly when only IDs are
     * available (e.g. Q&A attachments stored as string arrays).
     */
    async loadFilesWithIds(fileIds: string[]) {
        if (fileIds.length === 0) {
            this.files = []
            return
        }

        try {
            const companyId = await RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyIdAfterLoadingAffiliations()
            const metadata = (await SocketClient.RetrieveFilesMetadata({ fileIds, companyId })).result
            this.files = metadata ?? []
        }
        catch (error) {
            console.error("[RHFileListView] Failed to load file metadata:", error)
            this.files = []
        }
    }

    set downloadEnabled(enabled: boolean) {
        this._downloadEnabled = enabled
        this.updateFileViews()
    }

    get downloadEnabled(): boolean {
        return this._downloadEnabled
    }

    set deleteEnabled(enabled: boolean) {
        this._deleteEnabled = enabled
        this.updateFileViews()
    }

    get deleteEnabled(): boolean {
        return this._deleteEnabled
    }

    private updateFileViews() {
        // Remove all existing file views
        this.fileViews.forEach(view => view.removeFromSuperview())
        this.fileViews = []

        if (this._files.length === 0) {
            this.emptyStateLabel.hidden = NO
        }
        else {
            this.emptyStateLabel.hidden = YES

            // Create a new RHFileItemView for each file
            this._files.forEach(fileData => {
                const fileView = new RHFileItemView(undefined, (deletedView) => {
                    // Handle file deletion
                    this.handleFileDeleted(deletedView)
                }).addedAsSubviewToView(this)

                fileView.variant = this._variant
                fileView.configureWithFile(
                    fileData,
                    this._downloadEnabled,
                    this._deleteEnabled
                )

                this.fileViews.push(fileView)
            })
        }

        this.setNeedsLayoutUpToRootView()
    }

    private handleFileDeleted(deletedView: RHFileItemView) {
        // Remove the view from our tracking array
        const index = this.fileViews.indexOf(deletedView)
        if (index > -1) {
            this.fileViews.splice(index, 1)
        }

        // Update the files array
        const fileId = deletedView.fileId
        this._files = this._files.filter(file => (referenceId(file._id) ?? file.id) !== fileId)

        // Show empty state if no files remain
        if (this.fileViews.length === 0) {
            this.emptyStateLabel.hidden = NO
        }

        this.setNeedsLayoutUpToRootView()
    }

    private _padding() {
        return this._variant === RHFileListView.variant.compactDownload
               ? RHForms.fileListCompactPadding
               : RHForms.fileListPadding
    }

    private _fileGap() {
        return this._variant === RHFileListView.variant.compactDownload
               ? RHForms.fileListCompactFileGap
               : RHForms.fileListFileGap
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this._padding()
        const bounds = this.contentBoundsWithInset(padding)

        if (this.fileViews.length === 0) {
            // Center the empty state label
            this.emptyStateLabel.centerInContainer()
        }
        else {
            // Layout file views in a column
            bounds.framesByDistributingViewsAsColumn(this.fileViews, this._fileGap())
        }
    }

    override intrinsicContentHeight(constrainingWidth = 0) {
        const padding = this._padding()
        const gap = this._fileGap()
        if (this.fileViews.length === 0) {
            return this.emptyStateLabel.intrinsicContentHeight(constrainingWidth) + padding * 2
        }

        const itemWidth = Math.max(0, constrainingWidth - padding * 2)
        return padding * 2
            + this.fileViews.reduce((sum, view) => sum + view.intrinsicContentHeight(itemWidth), 0)
            + Math.max(0, this.fileViews.length - 1) * gap
    }

}
